import React, { useState } from 'react';
import { X, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';

export function OConnellCenterInteractiveMap({ onLocationSelect, onClose }) {
  const [hoveredZone, setHoveredZone] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [showLabels, setShowLabels] = useState(true);
  const [visibleLayers, setVisibleLayers] = useState({
    quietZones: true,
    accessible: true,
    sensoryFriendly: true,
    exits: true,
    services: true,
    restrooms: true
  });

  const handleZoneClick = (zoneId, zoneName, zoneType) => {
    setSelectedZone({ id: zoneId, name: zoneName, type: zoneType });
    onLocationSelect?.(zoneId);
  };

  const toggleLayer = (layer) => {
    setVisibleLayers(prev => ({ ...prev, [layer]: !prev[layer] }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0021A5] to-[#FA4616] text-white p-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_8e534113-c2c4-4f3f-8864-185da76b04eb/artifacts/d11zdlcb_XK%20Inclusive%20GameDay%20Logo.png"
              alt="Logo"
              className="w-16 h-16 md:w-20 md:h-20 object-contain"
            />
            <div>
              <h2 className="text-lg md:text-xl font-bold" style={{ fontFamily: "Lexend, sans-serif" }}>O'Connell Center</h2>
              <p className="text-sm text-white/90 font-medium">XTERMIGATOR Kids Inclusive Game Day</p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Close map"
              data-testid="close-interactive-map"
            >
              <X size={24} />
            </button>
          )}
        </div>

        <div className="flex flex-1 overflow-hidden flex-col md:flex-row">
          {/* Sidebar Controls */}
          <div className="md:w-64 bg-gray-50 p-4 overflow-y-auto border-r order-2 md:order-1">
            <div className="space-y-4">
              {/* Zoom Controls */}
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <h3 className="font-bold text-sm text-gray-700 mb-2">View Controls</h3>
                <div className="flex md:flex-col gap-2">
                  <button
                    onClick={() => setZoom(Math.min(zoom + 0.2, 2))}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 rounded text-sm font-medium text-blue-700"
                    data-testid="zoom-in-btn"
                  >
                    <ZoomIn size={16} /> <span className="hidden md:inline">Zoom In</span>
                  </button>
                  <button
                    onClick={() => setZoom(Math.max(zoom - 0.2, 0.6))}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 rounded text-sm font-medium text-blue-700"
                    data-testid="zoom-out-btn"
                  >
                    <ZoomOut size={16} /> <span className="hidden md:inline">Zoom Out</span>
                  </button>
                  <button
                    onClick={() => setZoom(1)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium text-gray-700"
                    data-testid="reset-view-btn"
                  >
                    <Maximize2 size={16} /> <span className="hidden md:inline">Reset</span>
                  </button>
                </div>
              </div>

              {/* Layer Toggles */}
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <h3 className="font-bold text-sm text-gray-700 mb-2">Map Layers</h3>
                <div className="space-y-2">
                  <LayerToggle
                    label="Quiet Zones"
                    icon="🤫"
                    color="bg-green-500"
                    active={visibleLayers.quietZones}
                    onChange={() => toggleLayer('quietZones')}
                  />
                  <LayerToggle
                    label="Accessible Seating"
                    icon="♿"
                    color="bg-yellow-400"
                    active={visibleLayers.accessible}
                    onChange={() => toggleLayer('accessible')}
                  />
                  <LayerToggle
                    label="Sensory-Friendly"
                    icon="🎧"
                    color="bg-green-300"
                    active={visibleLayers.sensoryFriendly}
                    onChange={() => toggleLayer('sensoryFriendly')}
                  />
                  <LayerToggle
                    label="Emergency Exits"
                    icon="🚪"
                    color="bg-red-500"
                    active={visibleLayers.exits}
                    onChange={() => toggleLayer('exits')}
                  />
                  <LayerToggle
                    label="Services"
                    icon="ℹ️"
                    color="bg-blue-500"
                    active={visibleLayers.services}
                    onChange={() => toggleLayer('services')}
                  />
                  <LayerToggle
                    label="Restrooms"
                    icon="🚻"
                    color="bg-purple-500"
                    active={visibleLayers.restrooms}
                    onChange={() => toggleLayer('restrooms')}
                  />
                </div>
                <div className="mt-3 pt-3 border-t">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showLabels}
                      onChange={(e) => setShowLabels(e.target.checked)}
                      className="rounded"
                    />
                    Show Labels
                  </label>
                </div>
              </div>

              {/* Current Selection */}
              {selectedZone && (
                <div className="bg-blue-50 rounded-lg p-3 shadow-sm border-2 border-blue-300">
                  <h3 className="font-bold text-sm text-blue-900 mb-1">Selected</h3>
                  <p className="text-sm font-semibold text-blue-800">{selectedZone.name}</p>
                  <button
                    onClick={() => setSelectedZone(null)}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear Selection
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Main Map Area */}
          <div className="flex-1 overflow-auto bg-gray-100 p-4 order-1 md:order-2">
            <div 
              className="mx-auto transition-transform duration-300"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'center top' }}
            >
              <SVGFloorPlan
                hoveredZone={hoveredZone}
                selectedZone={selectedZone}
                onZoneHover={setHoveredZone}
                onZoneClick={handleZoneClick}
                visibleLayers={visibleLayers}
                showLabels={showLabels}
              />
            </div>

            {/* Hover Tooltip */}
            {hoveredZone && (
              <div className="fixed bottom-20 md:bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-xl z-50">
                <p className="font-semibold">{hoveredZone}</p>
                <p className="text-xs text-gray-300">Click for details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Layer Toggle Component
function LayerToggle({ label, icon, color, active, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
      <input
        type="checkbox"
        checked={active}
        onChange={onChange}
        className="rounded"
      />
      <div className={`w-4 h-4 rounded ${color}`}></div>
      <span className="text-sm">{icon}</span>
      <span className="text-sm text-gray-700 flex-1">{label}</span>
    </label>
  );
}

// Main SVG Floor Plan
function SVGFloorPlan({ 
  hoveredZone, 
  selectedZone, 
  onZoneHover, 
  onZoneClick, 
  visibleLayers,
  showLabels 
}) {
  const isSelected = (id) => selectedZone?.id === id;

  return (
    <svg 
      viewBox="0 0 1000 800" 
      className="w-full h-auto bg-white rounded-xl shadow-lg"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="O'Connell Center interactive floor plan"
    >
      {/* Background */}
      <rect width="1000" height="800" fill="#f8fafc"/>
      
      {/* Grid Pattern */}
      <defs>
        <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
          <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
        </pattern>
      </defs>
      <rect width="1000" height="800" fill="url(#grid)" opacity="0.5"/>

      {/* Main Arena Floor */}
      <ellipse 
        cx="500" 
        cy="400" 
        rx="280" 
        ry="200" 
        fill="#1e3a8a"
        opacity="0.15"
        stroke="#1e40af"
        strokeWidth="3"
        strokeDasharray="10,5"
      />
      
      {/* Basketball Court Markings */}
      <rect x="360" y="300" width="280" height="200" fill="none" stroke="#1e40af" strokeWidth="2" opacity="0.3"/>
      <circle cx="500" cy="400" r="50" fill="none" stroke="#1e40af" strokeWidth="2" opacity="0.3"/>
      <line x1="500" y1="300" x2="500" y2="500" stroke="#1e40af" strokeWidth="2" opacity="0.3"/>
      
      {showLabels && (
        <text x="500" y="410" textAnchor="middle" fill="#1e40af" fontSize="24" fontWeight="bold" opacity="0.7">
          Main Court
        </text>
      )}

      {/* ========== SEATING SECTIONS ========== */}

      {/* Section 101 - Accessible (Northwest) */}
      {visibleLayers.accessible && (
        <AccessibleSection
          id="ada-101"
          points="120,200 120,100 250,100 280,150 280,250 250,300 180,300"
          label="Section 101"
          sublabel="♿ Wheelchair"
          centerX={200}
          centerY={200}
          isSelected={isSelected('ada-101')}
          onHover={() => onZoneHover('Section 101 - Wheelchair Accessible')}
          onLeave={() => onZoneHover(null)}
          onClick={() => onZoneClick('ada-101', 'Section 101 - Wheelchair Companion', 'accessible')}
          showLabels={showLabels}
        />
      )}

      {/* Section 105 - Accessible (North) */}
      {visibleLayers.accessible && (
        <AccessibleSection
          id="ada-105"
          points="320,80 400,60 480,60 560,80 580,120 520,180 480,180 420,180 380,120"
          label="Section 105"
          sublabel="♿ Accessible"
          centerX={450}
          centerY={120}
          isSelected={isSelected('ada-105')}
          onHover={() => onZoneHover('Section 105 - Wheelchair Accessible')}
          onLeave={() => onZoneHover(null)}
          onClick={() => onZoneClick('ada-105', 'Section 105 - Wheelchair Accessible', 'accessible')}
          showLabels={showLabels}
        />
      )}

      {/* Section 109 - Sensory Friendly (East) */}
      {visibleLayers.sensoryFriendly && (
        <SensoryFriendlySection
          id="sensory-109"
          points="720,150 720,100 850,100 880,150 880,300 850,350 750,350 720,300"
          label="Section 109"
          sublabel="🎧 Sensory"
          centerX={800}
          centerY={225}
          isSelected={isSelected('sensory-109')}
          onHover={() => onZoneHover('Section 109 - Sensory-Friendly Zone')}
          onLeave={() => onZoneHover(null)}
          onClick={() => onZoneClick('sensory-109', 'Section 109 - Sensory-Friendly', 'sensory')}
          showLabels={showLabels}
        />
      )}

      {/* Section 201 - Upper Level Accessible */}
      {visibleLayers.accessible && (
        <AccessibleSection
          id="ada-201"
          points="600,80 680,60 760,60 840,80 860,120 800,180 760,180 700,180 660,120"
          label="Section 201"
          sublabel="♿ Upper"
          centerX={720}
          centerY={120}
          isSelected={isSelected('ada-201')}
          onHover={() => onZoneHover('Section 201 - Upper Level Accessible')}
          onLeave={() => onZoneHover(null)}
          onClick={() => onZoneClick('ada-201', 'Section 201 - Upper Level', 'accessible')}
          showLabels={showLabels}
        />
      )}

      {/* Section 113 - Standard (Southeast) */}
      <StandardSection
        points="720,500 720,450 850,450 880,500 880,600 850,650 750,650 720,600"
        label="113"
        centerX={800}
        centerY={550}
        onHover={() => onZoneHover('Section 113')}
        onLeave={() => onZoneHover(null)}
        showLabels={showLabels}
      />

      {/* Section 117 - Standard (Southwest) */}
      <StandardSection
        points="120,500 120,450 250,450 280,500 280,600 250,650 150,650 120,600"
        label="117"
        centerX={200}
        centerY={550}
        onHover={() => onZoneHover('Section 117')}
        onLeave={() => onZoneHover(null)}
        showLabels={showLabels}
      />

      {/* Section 121 - Standard (South) */}
      <StandardSection
        points="320,680 400,700 480,700 560,680 580,640 520,600 480,600 420,600 380,640"
        label="121"
        centerX={450}
        centerY={650}
        onHover={() => onZoneHover('Section 121')}
        onLeave={() => onZoneHover(null)}
        showLabels={showLabels}
      />

      {/* ========== QUIET ZONES ========== */}

      {visibleLayers.quietZones && (
        <>
          {/* North Sensory Room */}
          <QuietZone
            id="quiet-1"
            cx={500}
            cy={30}
            r={35}
            icon="🤫"
            label="North Sensory Room"
            isSelected={isSelected('quiet-1')}
            onHover={() => onZoneHover('North Sensory Room - Soundproof')}
            onLeave={() => onZoneHover(null)}
            onClick={() => onZoneClick('quiet-1', 'North Sensory Room', 'quiet')}
            showLabels={showLabels}
          />

          {/* South Wellness Suite */}
          <QuietZone
            id="quiet-2"
            cx={500}
            cy={770}
            r={35}
            icon="🧘"
            label="South Wellness Suite"
            isSelected={isSelected('quiet-2')}
            onHover={() => onZoneHover('South Wellness Suite - Meditation Space')}
            onLeave={() => onZoneHover(null)}
            onClick={() => onZoneClick('quiet-2', 'South Wellness Suite', 'quiet')}
            showLabels={showLabels}
          />

          {/* Family Care Room (West) */}
          <QuietZone
            id="quiet-3"
            cx={50}
            cy={400}
            r={30}
            icon="👨‍👩‍👧"
            label="Family Care"
            isSelected={isSelected('quiet-3')}
            onHover={() => onZoneHover('Family Care Room')}
            onLeave={() => onZoneHover(null)}
            onClick={() => onZoneClick('quiet-3', 'Family Care Room', 'quiet')}
            showLabels={showLabels}
          />
        </>
      )}

      {/* ========== SERVICES ========== */}

      {visibleLayers.services && (
        <>
          {/* Guest Services */}
          <ServiceIcon
            id="guest-main"
            x={920}
            y={380}
            icon="ℹ️"
            label="Guest Services"
            bgColor="#3b82f6"
            isSelected={isSelected('guest-main')}
            onHover={() => onZoneHover('Guest Services Desk')}
            onLeave={() => onZoneHover(null)}
            onClick={() => onZoneClick('guest-main', 'Main Guest Services', 'service')}
            showLabels={showLabels}
          />

          {/* Medical / First Aid */}
          <ServiceIcon
            id="medical-aid"
            x={50}
            y={200}
            icon="🏥"
            label="First Aid"
            bgColor="#ef4444"
            isSelected={isSelected('medical-aid')}
            onHover={() => onZoneHover('First Aid & Medical Station')}
            onLeave={() => onZoneHover(null)}
            onClick={() => onZoneClick('medical-aid', 'First Aid Station', 'medical')}
            showLabels={showLabels}
          />

          {/* Family Assistance */}
          <ServiceIcon
            id="family-assist"
            x={50}
            y={600}
            icon="👨‍👩‍👧‍👦"
            label="Family"
            bgColor="#8b5cf6"
            isSelected={isSelected('family-assist')}
            onHover={() => onZoneHover('Family Assistance Center')}
            onLeave={() => onZoneHover(null)}
            onClick={() => onZoneClick('family-assist', 'Family Assistance Center', 'service')}
            showLabels={showLabels}
          />
        </>
      )}

      {/* ========== RESTROOMS ========== */}

      {visibleLayers.restrooms && (
        <>
          <RestroomIcon
            id="restroom-north"
            x={150}
            y={50}
            label="Accessible"
            isSelected={isSelected('restroom-north')}
            onHover={() => onZoneHover('North Accessible Restroom')}
            onLeave={() => onZoneHover(null)}
            onClick={() => onZoneClick('restroom-north', 'North Accessible Restroom', 'restroom')}
            showLabels={showLabels}
          />

          <RestroomIcon
            id="restroom-south"
            x={850}
            y={750}
            label="Accessible"
            isSelected={isSelected('restroom-south')}
            onHover={() => onZoneHover('South Accessible Restroom')}
            onLeave={() => onZoneHover(null)}
            onClick={() => onZoneClick('restroom-south', 'South Accessible Restroom', 'restroom')}
            showLabels={showLabels}
          />
        </>
      )}

      {/* ========== EMERGENCY EXITS ========== */}

      {visibleLayers.exits && (
        <>
          <EmergencyExit x={470} y={5} label="EXIT A1" direction="north" />
          <EmergencyExit x={960} y={390} label="EXIT B1" direction="east" />
          <EmergencyExit x={470} y={785} label="EXIT C1" direction="south" />
          <EmergencyExit x={5} y={390} label="EXIT D1" direction="west" />
        </>
      )}

      {/* ========== COMPASS ========== */}
      <g transform="translate(940, 50)">
        <circle cx="0" cy="0" r="30" fill="white" stroke="#6b7280" strokeWidth="2" opacity="0.9"/>
        <text x="0" y="-18" textAnchor="middle" fill="#dc2626" fontSize="18" fontWeight="bold">N</text>
        <text x="0" y="24" textAnchor="middle" fill="#6b7280" fontSize="14">S</text>
        <polygon points="0,-12 -6,6 0,0 6,6" fill="#dc2626"/>
      </g>

      {/* ========== SCALE BAR ========== */}
      <g transform="translate(50, 780)">
        <line x1="0" y1="0" x2="100" y2="0" stroke="#374151" strokeWidth="3"/>
        <line x1="0" y1="-5" x2="0" y2="5" stroke="#374151" strokeWidth="3"/>
        <line x1="100" y1="-5" x2="100" y2="5" stroke="#374151" strokeWidth="3"/>
        <text x="50" y="-10" textAnchor="middle" fill="#374151" fontSize="12" fontWeight="bold">
          50 feet
        </text>
      </g>

      {/* ========== TITLE ========== */}
      <text x="500" y="25" textAnchor="middle" fill="#1e40af" fontSize="18" fontWeight="bold" style={{ fontFamily: "Lexend, sans-serif" }}>
        O'CONNELL CENTER - ACCESSIBILITY MAP
      </text>
    </svg>
  );
}

// ========== SUB-COMPONENTS ==========

function AccessibleSection({ id, points, label, sublabel, centerX, centerY, isSelected, onHover, onLeave, onClick, showLabels }) {
  return (
    <g>
      <polygon
        points={points}
        fill={isSelected ? "#fef3c7" : "#fef9c3"}
        stroke={isSelected ? "#f59e0b" : "#fbbf24"}
        strokeWidth={isSelected ? "4" : "3"}
        className="cursor-pointer hover:opacity-90 transition-all duration-200"
        style={{ filter: isSelected ? "drop-shadow(0 0 10px #f59e0b)" : "none" }}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        onClick={onClick}
        role="button"
        aria-label={`${label} - accessible seating`}
        tabIndex={0}
      />
      {showLabels && (
        <>
          <text x={centerX} y={centerY - 10} textAnchor="middle" fill="#92400e" fontSize="14" fontWeight="bold">
            {label}
          </text>
          <text x={centerX} y={centerY + 10} textAnchor="middle" fill="#b45309" fontSize="11">
            {sublabel}
          </text>
        </>
      )}
    </g>
  );
}

function SensoryFriendlySection({ id, points, label, sublabel, centerX, centerY, isSelected, onHover, onLeave, onClick, showLabels }) {
  return (
    <g>
      <polygon
        points={points}
        fill={isSelected ? "#d1fae5" : "#dcfce7"}
        stroke={isSelected ? "#10b981" : "#34d399"}
        strokeWidth={isSelected ? "4" : "3"}
        className="cursor-pointer hover:opacity-90 transition-all duration-200"
        style={{ filter: isSelected ? "drop-shadow(0 0 10px #10b981)" : "none" }}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        onClick={onClick}
        role="button"
        aria-label={`${label} - sensory friendly seating`}
        tabIndex={0}
      />
      {showLabels && (
        <>
          <text x={centerX} y={centerY - 10} textAnchor="middle" fill="#047857" fontSize="14" fontWeight="bold">
            {label}
          </text>
          <text x={centerX} y={centerY + 10} textAnchor="middle" fill="#059669" fontSize="11">
            {sublabel}
          </text>
        </>
      )}
    </g>
  );
}

function StandardSection({ points, label, centerX, centerY, onHover, onLeave, showLabels }) {
  return (
    <g>
      <polygon
        points={points}
        fill="#e5e7eb"
        stroke="#9ca3af"
        strokeWidth="2"
        className="cursor-pointer hover:fill-gray-300 transition-colors duration-200"
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
      />
      {showLabels && (
        <text x={centerX} y={centerY} textAnchor="middle" fill="#4b5563" fontSize="16" fontWeight="bold">
          {label}
        </text>
      )}
    </g>
  );
}

function QuietZone({ id, cx, cy, r, icon, label, isSelected, onHover, onLeave, onClick, showLabels }) {
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={isSelected ? "#86efac" : "#bbf7d0"}
        stroke={isSelected ? "#16a34a" : "#22c55e"}
        strokeWidth={isSelected ? "4" : "3"}
        className="cursor-pointer hover:opacity-90 transition-all duration-200"
        style={{ filter: isSelected ? "drop-shadow(0 0 12px #22c55e)" : "none" }}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        onClick={onClick}
        role="button"
        aria-label={label}
        tabIndex={0}
      />
      <text x={cx} y={cy + 5} textAnchor="middle" fontSize="20">
        {icon}
      </text>
      {showLabels && (
        <text x={cx} y={cy + r + 15} textAnchor="middle" fill="#166534" fontSize="10" fontWeight="bold">
          {label}
        </text>
      )}
    </g>
  );
}

function ServiceIcon({ id, x, y, icon, label, bgColor, isSelected, onHover, onLeave, onClick, showLabels }) {
  return (
    <g>
      <rect
        x={x - 20}
        y={y - 20}
        width="40"
        height="40"
        rx="8"
        fill={bgColor}
        stroke={isSelected ? "#1e3a8a" : "white"}
        strokeWidth={isSelected ? "4" : "2"}
        className="cursor-pointer hover:opacity-90 transition-all duration-200"
        style={{ filter: isSelected ? "drop-shadow(0 0 10px #3b82f6)" : "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        onClick={onClick}
        role="button"
        aria-label={label}
        tabIndex={0}
      />
      <text x={x} y={y + 6} textAnchor="middle" fontSize="18">
        {icon}
      </text>
      {showLabels && (
        <text x={x} y={y + 35} textAnchor="middle" fill="#1f2937" fontSize="10" fontWeight="bold">
          {label}
        </text>
      )}
    </g>
  );
}

function RestroomIcon({ id, x, y, label, isSelected, onHover, onLeave, onClick, showLabels }) {
  return (
    <g>
      <rect
        x={x - 18}
        y={y - 18}
        width="36"
        height="36"
        rx="6"
        fill={isSelected ? "#c4b5fd" : "#a78bfa"}
        stroke={isSelected ? "#6d28d9" : "#7c3aed"}
        strokeWidth={isSelected ? "3" : "2"}
        className="cursor-pointer hover:opacity-90 transition-all duration-200"
        style={{ filter: isSelected ? "drop-shadow(0 0 8px #8b5cf6)" : "drop-shadow(0 2px 4px rgba(0,0,0,0.15))" }}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        onClick={onClick}
        role="button"
        aria-label={`${label} Restroom`}
        tabIndex={0}
      />
      <text x={x} y={y + 5} textAnchor="middle" fontSize="16">
        🚻
      </text>
      {showLabels && (
        <text x={x} y={y + 30} textAnchor="middle" fill="#4c1d95" fontSize="9" fontWeight="bold">
          {label}
        </text>
      )}
    </g>
  );
}

function EmergencyExit({ x, y, label, direction }) {
  const getArrow = () => {
    switch(direction) {
      case 'north': return '↑';
      case 'south': return '↓';
      case 'east': return '→';
      case 'west': return '←';
      default: return '';
    }
  };

  return (
    <g>
      <rect
        x={x - 25}
        y={y - 10}
        width="50"
        height="20"
        rx="4"
        fill="#22c55e"
        stroke="white"
        strokeWidth="2"
      />
      <text x={x} y={y + 5} textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">
        {label}
      </text>
      <text x={direction === 'east' ? x + 35 : direction === 'west' ? x - 35 : x} 
            y={direction === 'north' ? y - 15 : direction === 'south' ? y + 25 : y + 5} 
            textAnchor="middle" fill="#16a34a" fontSize="14" fontWeight="bold">
        {getArrow()}
      </text>
    </g>
  );
}
